(function () {
  "use strict";

  var CONFIG = Object.assign(
    {
      devOnly: true,
      devModeDefaultEnabled: false,
      defaultMode: "incl",
      sourcePricesIncludeVat: false,
      fallbackCountry: "",
      fallbackCurrency: "EUR",
      euCountryEnabled: {},
      labels: {
        incl: "Incl. VAT",
        excl: "Excl. VAT",
      },
      euVatRates: {},
    },
    window.CRD_VAT_CONFIG || {},
  );

  var DEV_KEY = "crd_vat_dev_enabled";
  var MODE_KEY = "crd_vat_mode";
  var COUNTRY_ENABLE_KEY = "crd_vat_country_enabled_overrides";
  var LAST_COUNTRY_KEY = "crd_vat_last_country";

  var savedDev = safeStorageGet(DEV_KEY);
  var devEnabled =
    savedDev === null ? !!CONFIG.devModeDefaultEnabled : savedDev === "1";
  var countryEnableOverrides = parseJsonObjectSafe(
    safeStorageGet(COUNTRY_ENABLE_KEY),
  );

  exposeApi();

  if (CONFIG.devOnly && !devEnabled) {
    return;
  }

  var state = {
    country: detectCountryCode(),
    mode: normalizeMode(safeStorageGet(MODE_KEY) || CONFIG.defaultMode),
    ui: null,
    uiButton: null,
    running: false,
    scheduled: false,
    ignoreObserverUntil: 0,
    sourceSeq: 0,
    nodeMeta: new Map(),
    specialNodeMeta: new Map(),
    roots: new Set([document]),
    observer: null,
    lastScanAt: 0,
    lastShadowScanAt: 0,
    shadowObservers: [],
    observedShadowRoots: new WeakSet(),
  };

  // CRDBAG perf 2026-10-28: scans used to run on (almost) every DOM change on the page, walk every text node
  // of the whole document and re-search all ~15k elements for shadow roots each time (15-27 s main-thread time
  // on mobile product pages). Now: rescan only when a price-related node changes, only walk price containers,
  // at most every MIN_SCAN_GAP_MS, and do the full shadow-root search at most every SHADOW_SCAN_GAP_MS.
  var MIN_SCAN_GAP_MS = 200;
  var SHADOW_SCAN_GAP_MS = 3000;

  initUI();
  initListeners();
  // The selector constants are assigned at the end of this IIFE, so the first real scan must run after it.
  window.setTimeout(function () {
    scheduleScan(true);
  }, 0);
  window.addEventListener("load", function () {
    scheduleScan(true);
  });

  function exposeApi() {
    window.CRDVAT = {
      enableDevMode: function () {
        safeStorageSet(DEV_KEY, "1");
        window.location.reload();
      },
      disableDevMode: function () {
        safeStorageSet(DEV_KEY, "0");
        window.location.reload();
      },
      setMode: function (mode) {
        var normalized = normalizeMode(mode);
        safeStorageSet(MODE_KEY, normalized);
        if (state) {
          state.mode = normalized;
          updateToggle();
          scheduleScan();
        }
      },
      setCountryVatEnabled: function (countryCode, enabled) {
        var normalized = normalizeCountry(countryCode);
        if (!normalized) {
          return false;
        }

        countryEnableOverrides[normalized] = !!enabled;
        safeStorageSet(
          COUNTRY_ENABLE_KEY,
          JSON.stringify(countryEnableOverrides),
        );

        if (state) {
          updateToggle();
          scheduleScan(true);
        }

        return true;
      },
      clearCountryVatEnabled: function (countryCode) {
        var normalized = normalizeCountry(countryCode);
        if (!normalized) {
          return false;
        }

        delete countryEnableOverrides[normalized];
        safeStorageSet(
          COUNTRY_ENABLE_KEY,
          JSON.stringify(countryEnableOverrides),
        );

        if (state) {
          updateToggle();
          scheduleScan(true);
        }

        return true;
      },
      resetCountryVatOverrides: function () {
        countryEnableOverrides = {};
        safeStorageSet(
          COUNTRY_ENABLE_KEY,
          JSON.stringify(countryEnableOverrides),
        );

        if (state) {
          updateToggle();
          scheduleScan(true);
        }

        return true;
      },
      getCountryVatSettings: function () {
        var settings = {};
        var keys = Object.keys(CONFIG.euVatRates || {});
        for (var i = 0; i < keys.length; i += 1) {
          var code = normalizeCountry(keys[i]);
          if (!code) {
            continue;
          }

          settings[code] = {
            rate: getVatRate(code),
            enabled: isCountryVatEnabled(code),
            source: hasOwn(countryEnableOverrides, code)
              ? "localOverride"
              : hasOwn(CONFIG.euCountryEnabled || {}, code)
                ? "config"
                : "default",
          };
        }

        return settings;
      },
      status: function () {
        return {
          active: !(CONFIG.devOnly && !devEnabled),
          devOnly: !!CONFIG.devOnly,
          devEnabled: devEnabled,
          mode: state
            ? getEffectiveMode()
            : normalizeMode(safeStorageGet(MODE_KEY) || CONFIG.defaultMode),
          selectedCountry: state ? state.country : detectCountryCode(),
          isEuCountry: isEuCountry(state ? state.country : detectCountryCode()),
          vatEnabledForCountry: isCountryVatEnabled(
            state ? state.country : detectCountryCode(),
          ),
          countryOverrides: Object.assign({}, countryEnableOverrides),
        };
      },
      reprocess: function () {
        if (state) {
          scheduleScan(true);
        }
      },
    };
  }

  function initUI() {
    var wrap = document.createElement("div");
    wrap.className = "crd-vat-switcher";
    wrap.setAttribute("data-crd-vat-ui", "1");
    wrap.innerHTML =
      '<span class="crd-vat-switcher__title">VAT</span>' +
      '<button type="button" class="crd-vat-switcher__toggle" aria-label="Toggle VAT display" aria-pressed="false">' +
      '<span class="crd-vat-switcher__text crd-vat-switcher__text--incl">Incl.</span>' +
      '<span class="crd-vat-switcher__text crd-vat-switcher__text--excl">Excl.</span>' +
      '<span class="crd-vat-switcher__knob" aria-hidden="true"></span>' +
      "</button>";

    document.body.appendChild(wrap);

    state.ui = wrap;
    state.uiButton = wrap.querySelector(".crd-vat-switcher__toggle");

    state.uiButton.addEventListener("click", function () {
      if (!isEuCountry(state.country)) {
        state.mode = "excl";
        safeStorageSet(MODE_KEY, "excl");
        updateToggle();
        return;
      }

      state.mode = state.mode === "incl" ? "excl" : "incl";
      safeStorageSet(MODE_KEY, state.mode);
      updateToggle();
      scheduleScan();
    });

    updateToggle();
  }

  function updateToggle() {
    if (!state.uiButton || !state.ui) {
      return;
    }

    var isEu = isEuCountry(state.country);
    var effectiveMode = getEffectiveMode();
    state.ui.hidden = !isEu;

    state.uiButton.classList.toggle("is-incl", effectiveMode === "incl");
    state.uiButton.classList.toggle("is-excl", effectiveMode === "excl");
    state.uiButton.classList.toggle("is-locked", !isEu);
    state.uiButton.setAttribute(
      "aria-pressed",
      effectiveMode === "incl" ? "true" : "false",
    );
    state.uiButton.setAttribute("aria-disabled", !isEu ? "true" : "false");
  }

  function initListeners() {
    document.addEventListener("click", onCountryInteraction, true);
    document.addEventListener("change", onCountryInteraction, true);
    // Drawers / variant pickers can reveal prices without a price mutation - rescan shortly after interactions.
    document.addEventListener(
      "click",
      function () {
        window.setTimeout(function () {
          scheduleScan();
        }, 80);
      },
      true,
    );

    state.observer = new MutationObserver(function (mutations) {
      if (Date.now() < state.ignoreObserverUntil) {
        return;
      }

      var shouldRescan = false;
      for (var i = 0; i < mutations.length; i += 1) {
        var mutation = mutations[i];

        if (mutation.type === "childList") {
          for (var j = 0; j < mutation.addedNodes.length; j += 1) {
            var added = mutation.addedNodes[j];
            registerShadowRoots(added);
            if (!shouldRescan && containsPriceRelevantNode(added)) {
              shouldRescan = true;
            }
          }
          if (!shouldRescan && isPriceRelevantNode(mutation.target)) {
            shouldRescan = true;
          }
        }

        if (
          mutation.type === "characterData" &&
          !shouldRescan &&
          isPriceRelevantNode(mutation.target)
        ) {
          shouldRescan = true;
        }
        // "attributes" mutations (aria-current / data-country / value) only matter for country detection,
        // which updateCountryIfNeeded() below handles.
      }

      if (updateCountryIfNeeded()) {
        shouldRescan = true;
      }

      if (shouldRescan) {
        scheduleScan();
      }
    });

    state.observer.observe(document.documentElement, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ["aria-current", "data-country", "value"],
    });

    registerShadowRoots(document.documentElement);

    setInterval(function () {
      if (document.hidden) {
        return;
      }
      var changed = updateCountryIfNeeded();
      cleanupMeta();
      // Cheap safety-net rescan (only price containers are walked) so nothing stays unconverted.
      if (changed || Date.now() - state.lastScanAt > 1500) {
        scheduleScan();
      }
    }, 600);
  }

  function onCountryInteraction(event) {
    var target = event.target;
    if (!target || !target.closest) {
      return;
    }

    var relevant = target.closest(
      '.md-form__select__country__list-link[data-country], [id^="orbe-country-"], [name="country_code"], [name="country"], #country, #Country, #CountryCode',
    );

    if (!relevant) {
      return;
    }

    var explicitCountry = extractCountryCodeFromNode(relevant);
    if (explicitCountry && explicitCountry !== state.country) {
      state.country = explicitCountry;
      persistLastCountry(state.country);
      updateToggle();
      scheduleScan(true);
    }

    setTimeout(function () {
      if (updateCountryIfNeeded()) {
        scheduleScan(true);
      }
    }, 10);
  }

  function updateCountryIfNeeded() {
    var next = detectCountryCode();
    if (!next || next === state.country) {
      return false;
    }

    state.country = next;
    persistLastCountry(state.country);
    updateToggle();
    return true;
  }

  function isPriceRelevantNode(node) {
    var element = node && node.nodeType === 1 ? node : node && node.parentElement;
    return !!(element && element.closest && element.closest(RELEVANT_CONTEXT_SELECTOR));
  }

  function containsPriceRelevantNode(node) {
    if (!node) {
      return false;
    }
    if (node.nodeType === 3) {
      return isPriceRelevantNode(node);
    }
    if (node.nodeType !== 1) {
      return false;
    }
    if (node.closest && node.closest("[data-crd-vat-ui], .crd-vat-shadow-node")) {
      return false;
    }
    return (
      isPriceRelevantNode(node) ||
      !!(node.querySelector && node.querySelector(RELEVANT_CONTEXT_SELECTOR))
    );
  }

  function detectCountryCode() {
    var fromUrl = extractCountryCodeFromUrl();
    if (fromUrl) {
      return fromUrl;
    }

    var fromShopify = normalizeIso2CountryCode(
      window.Shopify && typeof window.Shopify.country === "string"
        ? window.Shopify.country
        : "",
    );
    if (fromShopify) {
      return fromShopify;
    }

    var fromOrbeCurrent = document.querySelector(
      '.md-form__select__country__list-link[aria-current="true"], [id^="orbe-country-"][aria-current="true"], [id^="orbe-country-"] [aria-current="true"]',
    );
    var fromOrbeCurrentCode = extractCountryCodeFromNode(fromOrbeCurrent);
    if (fromOrbeCurrentCode) {
      return fromOrbeCurrentCode;
    }

    var fromInput = document.querySelector(
      '[name="country_code"], [name="country"], #country, #Country, #CountryCode, #orbe-country-selector-popup-id',
    );
    if (fromInput && fromInput.value) {
      var normalizedFromInput = normalizeIso2CountryCode(fromInput.value);
      if (normalizedFromInput) {
        return normalizedFromInput;
      }
    }

    var fromInputAttrs = extractCountryCodeFromNode(fromInput);
    if (fromInputAttrs) {
      return fromInputAttrs;
    }

    var fromLastKnown = getPersistedLastCountry();
    if (fromLastKnown) {
      return fromLastKnown;
    }

    var bodyClassCode = extractCountryCodeFromClassName(
      document.body && document.body.className,
    );
    if (bodyClassCode) {
      return bodyClassCode;
    }

    if (typeof CONFIG.fallbackCountry === "string" && CONFIG.fallbackCountry) {
      return normalizeCountry(CONFIG.fallbackCountry);
    }

    return "SE";
  }

  function normalizeIso2CountryCode(value) {
    var raw = String(value || "").trim();
    if (!/^[A-Za-z]{2}$/.test(raw)) {
      return "";
    }

    return normalizeCountry(raw);
  }

  function getPersistedLastCountry() {
    return normalizeIso2CountryCode(safeStorageGet(LAST_COUNTRY_KEY));
  }

  function persistLastCountry(countryCode) {
    var normalized = normalizeIso2CountryCode(countryCode);
    if (!normalized) {
      return;
    }

    safeStorageSet(LAST_COUNTRY_KEY, normalized);
  }

  function extractCountryCodeFromNode(node) {
    if (!node || !node.getAttribute) {
      return "";
    }

    var attrCandidates = [
      "data-country",
      "data-value",
      "data-country-code",
      "value",
    ];
    for (var i = 0; i < attrCandidates.length; i += 1) {
      var rawAttrValue = node.getAttribute(attrCandidates[i]);
      var normalizedAttrValue = normalizeIso2CountryCode(rawAttrValue);
      if (normalizedAttrValue) {
        return normalizedAttrValue;
      }
    }

    var nodeId = String(node.id || "");
    var fromNodeId = extractCountryCodeFromOrbeId(nodeId);
    if (fromNodeId) {
      return fromNodeId;
    }

    if (node.closest) {
      var li = node.closest('[id^="orbe-country-"]');
      if (li && li.id) {
        var fromClosestId = extractCountryCodeFromOrbeId(li.id);
        if (fromClosestId) {
          return fromClosestId;
        }
      }
    }

    return "";
  }

  function extractCountryCodeFromOrbeId(value) {
    var raw = String(value || "").trim();
    if (!raw) {
      return "";
    }

    var match = raw.match(/^orbe-country-([A-Za-z]{2})$/i);
    if (!match) {
      return "";
    }

    return normalizeCountry(match[1]);
  }

  function extractCountryCodeFromClassName(value) {
    var raw = String(value || "");
    if (!raw) {
      return "";
    }

    var match = raw.match(/(?:^|\s)orbe-country-code--([A-Za-z]{2})(?:\s|$)/i);
    if (!match) {
      return "";
    }

    return normalizeCountry(match[1]);
  }

  function extractCountryCodeFromUrl() {
    try {
      var params = new URLSearchParams(window.location.search || "");
      var keys = ["country", "country_code", "Country", "CountryCode"];

      for (var i = 0; i < keys.length; i += 1) {
        var raw = params.get(keys[i]);
        var normalized = normalizeIso2CountryCode(raw);
        if (normalized) {
          return normalized;
        }
      }
    } catch (_error) {
      return "";
    }

    return "";
  }

  function getEffectiveMode() {
    if (!isEuCountry(state.country)) {
      return "excl";
    }
    return normalizeMode(state.mode);
  }

  function runFullScan() {
    if (state.running) {
      return;
    }

    state.running = true;
    state.ignoreObserverUntil = Date.now() + 120;

    try {
      if (Date.now() - state.lastShadowScanAt > SHADOW_SCAN_GAP_MS) {
        state.lastShadowScanAt = Date.now();
        registerShadowRoots(document.documentElement);
      }
      var roots = Array.from(state.roots);
      for (var i = 0; i < roots.length; i += 1) {
        scanRoot(roots[i]);
      }
      syncUpezDistanceValues();
      syncClvShadowValues();
    } finally {
      state.running = false;
      state.scheduled = false;
      state.lastScanAt = Date.now();
      if (state.observer) {
        // Drop the mutation records caused by this scan itself.
        state.observer.takeRecords();
      }
      for (var k = 0; k < state.shadowObservers.length; k += 1) {
        state.shadowObservers[k].takeRecords();
      }
    }
  }

  function scheduleScan(force) {
    if (force) {
      state.scheduled = false;
    }

    if (state.scheduled) {
      return;
    }

    state.scheduled = true;
    var wait = force
      ? 0
      : Math.max(0, state.lastScanAt + MIN_SCAN_GAP_MS - Date.now());
    if (wait > 0) {
      window.setTimeout(function () {
        window.requestAnimationFrame(runFullScan);
      }, wait);
    } else {
      window.requestAnimationFrame(runFullScan);
    }
  }

  function scanRoot(root) {
    if (!root) {
      return;
    }

    if (!root.querySelectorAll) {
      return;
    }

    // Upnova (cart drawer + PDP widgets) renders with generic utility classes, so its whole shadow root
    // is treated as one price context (CRDBAG 2026-11-02: the old .upez--* classes no longer exist).
    if (isUpnovaRoot(root)) {
      var upnovaWalker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
      var upnovaNode;
      while ((upnovaNode = upnovaWalker.nextNode())) {
        processTextNode(upnovaNode);
      }
      return;
    }

    // Text nodes outside PRICE_CONTEXT_SELECTOR are skipped by shouldSkipNode() anyway,
    // so only walk the price containers (deduplicated for nested containers).
    var containers = root.querySelectorAll(PRICE_CONTEXT_SELECTOR);
    var seen = new Set();
    for (var i = 0; i < containers.length; i += 1) {
      var walker = document.createTreeWalker(
        containers[i],
        NodeFilter.SHOW_TEXT,
        null,
      );
      var node;
      while ((node = walker.nextNode())) {
        if (seen.has(node)) {
          continue;
        }
        seen.add(node);
        processTextNode(node);
      }
    }
  }

  function processTextNode(node) {
    if (!node || !node.nodeValue) {
      return;
    }

    if (shouldSkipNode(node)) {
      if (state.nodeMeta.has(node)) {
        state.nodeMeta.delete(node);
      }
      clearNodeLabel(node);
      return;
    }

    var current = String(node.nodeValue);
    var meta = state.nodeMeta.get(node);

    if (!meta || current !== meta.lastApplied) {
      var sourceText = stripVatLabels(current);
      var parsed = parsePriceTokens(sourceText, state.country);

      if (!parsed.tokens.length) {
        state.nodeMeta.delete(node);
        clearNodeLabel(node);
        return;
      }

      meta = {
        sourceText: sourceText,
        tokens: parsed.tokens,
        lastApplied: "",
      };

      state.nodeMeta.set(node, meta);
    }

    var next = renderText(meta);
    if (next !== current) {
      node.nodeValue = next;
    }

    meta.lastApplied = next;
    applyNodeLabel(node, getCurrentLabelText());
  }

  function shouldSkipNode(node) {
    if (!node.parentElement) {
      return true;
    }

    var parent = node.parentElement;

    if (parent.closest(SKIP_CONTEXT_SELECTOR)) {
      return true;
    }

    if (parent.closest(KACHING_CONTEXT_SELECTOR)) {
      return true;
    }

    var inUpnova = isUpnovaElement(parent);

    if (!inUpnova && !parent.closest(PRICE_CONTEXT_SELECTOR)) {
      return true;
    }

    // Upnova keeps invisible copies of prices for its animations; convert them too so they match when shown.
    if (!inUpnova && !isElementVisible(parent)) {
      return true;
    }

    var value = String(node.nodeValue || "").trim();
    if (!value) {
      return true;
    }

    if (!/(\u20AC|\$|\u00A3|\bkr\b|\bSEK\b|\bEUR\b|\bUSD\b)/i.test(value)) {
      return true;
    }

    return false;
  }

  function renderText(meta) {
    return renderTextInternal(meta, false);
  }

  function renderTextWithoutLabel(meta) {
    return renderTextInternal(meta, false);
  }

  function renderTextInternal(meta, includeLabel) {
    var source = meta.sourceText;
    var pieces = [];
    var cursor = 0;

    for (var i = 0; i < meta.tokens.length; i += 1) {
      var token = meta.tokens[i];
      pieces.push(source.slice(cursor, token.start));
      pieces.push(formatToken(token, includeLabel));
      cursor = token.end;
    }

    pieces.push(source.slice(cursor));
    return cleanupRedundantCurrencyCodes(pieces.join(""));
  }

  function formatToken(token, includeLabel) {
    var isEu = isEuCountry(state.country);
    var rate = getVatRate(state.country);
    var mode = getEffectiveMode();

    var value = token.netAmount;
    if (isEu && mode === "incl") {
      value = token.netAmount * (1 + rate);
    }

    var formattedNumber = formatNumber(value, token.numberFormat);
    var normalizedSuffix = normalizeCurrencySuffix(token.prefix, token.suffix);
    var out = token.prefix + formattedNumber + normalizedSuffix;
    if (!includeLabel) {
      return out;
    }

    return out + " " + getCurrentLabelText();
  }

  function normalizeCurrencySuffix(prefix, suffix) {
    var safePrefix = String(prefix || "");
    var safeSuffix = String(suffix || "");

    if (!/[€$£¥]/.test(safePrefix)) {
      return safeSuffix;
    }

    return safeSuffix.replace(
      /(?:\s|\u00A0)*(?:EUR|USD|GBP|JPY|CAD|AUD|SEK|NOK|DKK)\b/gi,
      "",
    );
  }

  function cleanupRedundantCurrencyCodes(text) {
    return String(text || "").replace(
      /([€$£¥]\s*-?\d[\d\s.,]*\d)\s*(?:EUR|USD|GBP|JPY|CAD|AUD|SEK|NOK|DKK)\b/gi,
      "$1",
    );
  }

  function parsePriceTokens(text, sourceCountry) {
    var tokens = [];
    var matches = text.matchAll(MONEY_TOKEN_REGEX);
    var sourceRate = getVatRate(sourceCountry);
    var includeVatAtSource = !!CONFIG.sourcePricesIncludeVat && sourceRate > 0;

    for (
      var _iterator = matches[Symbol.iterator](), _step;
      !(_step = _iterator.next()).done;

    ) {
      var match = _step.value;
      var rawToken = match[0];
      var start = match.index;
      if (typeof start !== "number") {
        continue;
      }

      var parsed = parseToken(rawToken);
      if (!parsed) {
        continue;
      }

      var netAmount = parsed.amount;
      if (includeVatAtSource) {
        netAmount = parsed.amount / (1 + sourceRate);
      }

      tokens.push({
        start: start,
        end: start + rawToken.length,
        prefix: parsed.prefix,
        suffix: parsed.suffix,
        numberFormat: parsed.numberFormat,
        netAmount: netAmount,
      });
    }

    return { tokens: tokens };
  }

  function parseToken(tokenText) {
    var cleaned = String(tokenText || "")
      .replace(/\u00A0/g, " ")
      .trim();
    if (!cleaned) {
      return null;
    }

    var numberMatch = cleaned.match(/-?\d[\d\s.,]*\d|-?\d/);
    if (!numberMatch) {
      return null;
    }

    var numberText = numberMatch[0];
    var numberStart = numberMatch.index || 0;
    var numberEnd = numberStart + numberText.length;

    var prefix = cleaned.slice(0, numberStart);
    var suffix = cleaned.slice(numberEnd);

    if (
      !/(\u20AC|\$|\u00A3|\bkr\b|\bSEK\b|\bEUR\b|\bUSD\b)/i.test(
        prefix + suffix,
      )
    ) {
      return null;
    }

    var numberFormat = parseNumberFormat(numberText);
    if (!numberFormat) {
      return null;
    }

    return {
      prefix: prefix,
      suffix: suffix,
      numberFormat: numberFormat,
      amount: numberFormat.value,
    };
  }

  function parseNumberFormat(numberText) {
    var source = String(numberText || "").replace(/\u00A0/g, " ");
    if (!source) {
      return null;
    }

    var raw = source.replace(/\s/g, "");
    if (!raw) {
      return null;
    }

    var lastComma = raw.lastIndexOf(",");
    var lastDot = raw.lastIndexOf(".");

    var decimalSep = null;
    if (lastComma !== -1 && lastDot !== -1) {
      decimalSep = lastComma > lastDot ? "," : ".";
    } else if (lastComma !== -1) {
      var commaFraction = raw.length - lastComma - 1;
      if (commaFraction === 1 || commaFraction === 2) {
        decimalSep = ",";
      }
    } else if (lastDot !== -1) {
      var dotFraction = raw.length - lastDot - 1;
      if (dotFraction === 1 || dotFraction === 2) {
        decimalSep = ".";
      }
    }

    var decimals = 0;
    var groupSep = "";

    if (decimalSep) {
      var splitRaw = raw.split(decimalSep);
      decimals = splitRaw[1] ? splitRaw[1].length : 0;

      var intPartSource = source.split(decimalSep)[0] || source;
      var groupMatch = intPartSource.match(/[.,\s](?=\d{3}(?:\D|$))/);
      groupSep = groupMatch ? groupMatch[0] : "";
    } else {
      var groupedMatch = source.match(/[.,\s](?=\d{3}(?:\D|$))/);
      groupSep = groupedMatch ? groupedMatch[0] : "";
    }

    var normalized = raw;
    if (decimalSep) {
      var thousandsRegex = decimalSep === "," ? /\./g : /,/g;
      normalized = normalized.replace(thousandsRegex, "");
      normalized = normalized.replace(decimalSep, ".");
    } else {
      normalized = normalized.replace(/[.,]/g, "");
    }

    var value = Number(normalized);
    if (!Number.isFinite(value)) {
      return null;
    }

    return {
      value: value,
      decimals: decimals,
      decimalSep: decimalSep || ",",
      groupSep: groupSep || "",
    };
  }

  function formatNumber(value, format) {
    var decimals = Math.max(0, Number(format.decimals) || 0);
    var decimalSep = format.decimalSep || ",";
    var groupSep = format.groupSep || "";

    var safeValue = Number(value);
    if (!Number.isFinite(safeValue)) {
      safeValue = 0;
    }

    var negative = safeValue < 0;
    var fixed = Math.abs(safeValue).toFixed(decimals);
    var parts = fixed.split(".");
    var intPart = parts[0];
    var fracPart = parts[1] || "";

    if (groupSep) {
      intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, groupSep);
    }

    var out = decimals > 0 ? intPart + decimalSep + fracPart : intPart;
    return negative ? "-" + out : out;
  }

  function cleanupMeta() {
    var staleNodes = [];
    state.nodeMeta.forEach(function (_meta, node) {
      if (!node || !node.isConnected) {
        staleNodes.push(node);
      }
    });

    for (var i = 0; i < staleNodes.length; i += 1) {
      state.nodeMeta.delete(staleNodes[i]);
    }

    var staleSpecialNodes = [];
    state.specialNodeMeta.forEach(function (_meta, node) {
      if (!node || !node.isConnected) {
        staleSpecialNodes.push(node);
      }
    });

    for (var j = 0; j < staleSpecialNodes.length; j += 1) {
      state.specialNodeMeta.delete(staleSpecialNodes[j]);
    }
  }

  function registerShadowRoots(node) {
    if (!node || node.nodeType !== 1) {
      return;
    }

    if (node.shadowRoot) {
      addShadowRoot(node.shadowRoot);
    }

    if (!node.querySelectorAll) {
      return;
    }

    var all = node.querySelectorAll("*");
    for (var i = 0; i < all.length; i += 1) {
      if (all[i].shadowRoot) {
        addShadowRoot(all[i].shadowRoot);
      }
    }
  }

  function addShadowRoot(root) {
    state.roots.add(root);
    if (!isUpnovaRoot(root) || state.observedShadowRoots.has(root)) {
      return;
    }
    // The document observer cannot see inside shadow roots: watch Upnova's own tree so re-rendered
    // (excl. VAT) prices are converted again right away instead of on the 1.5 s safety-net scan.
    state.observedShadowRoots.add(root);
    var observer = new MutationObserver(function () {
      if (Date.now() < state.ignoreObserverUntil) {
        return;
      }
      scheduleScan();
    });
    observer.observe(root, { subtree: true, childList: true, characterData: true });
    state.shadowObservers.push(observer);
  }

  function isUpnovaRoot(root) {
    // localName instead of a selector constant: this also runs before the constants at the end are assigned.
    return !!(root && root.host && root.host.localName === "upnova-widget");
  }

  function isUpnovaElement(element) {
    var root = element && element.getRootNode ? element.getRootNode() : null;
    return isUpnovaRoot(root);
  }

  function isEuCountry(countryCode) {
    return getVatRate(countryCode) > 0 && isCountryVatEnabled(countryCode);
  }

  function isCountryVatEnabled(countryCode) {
    var normalized = normalizeCountry(countryCode);
    if (!normalized) {
      return false;
    }

    if (hasOwn(countryEnableOverrides, normalized)) {
      var override = countryEnableOverrides[normalized];
      return !(
        override === false ||
        override === "false" ||
        override === 0 ||
        override === "0"
      );
    }

    var fromConfig = CONFIG.euCountryEnabled || {};
    if (hasOwn(fromConfig, normalized)) {
      return fromConfig[normalized] !== false;
    }

    return true;
  }

  function getVatRate(countryCode) {
    var normalized = normalizeCountry(countryCode);
    if (!normalized) {
      return 0;
    }

    if (normalized === "EL") {
      normalized = "GR";
    }

    var rate = CONFIG.euVatRates[normalized];
    return typeof rate === "number" && rate > 0 ? rate : 0;
  }

  function normalizeCountry(value) {
    return String(value || "")
      .trim()
      .slice(0, 2)
      .toUpperCase();
  }

  function normalizeMode(value) {
    return String(value || "").toLowerCase() === "excl" ? "excl" : "incl";
  }

  function isElementVisible(element) {
    if (!element || !element.ownerDocument) {
      return false;
    }

    if (typeof element.checkVisibility === "function") {
      if (!element.checkVisibility({ visibilityProperty: true })) {
        return false;
      }
      return !element.closest('[hidden], [aria-hidden="true"]');
    }

    var current = element;
    while (current && current.nodeType === 1) {
      var style = window.getComputedStyle(current);
      if (
        style &&
        (style.display === "none" || style.visibility === "hidden")
      ) {
        return false;
      }

      if (current.hasAttribute && current.hasAttribute("hidden")) {
        return false;
      }

      if (
        current.getAttribute &&
        current.getAttribute("aria-hidden") === "true"
      ) {
        return false;
      }

      current = current.parentElement;
    }

    return true;
  }

  function getCurrentLabelText() {
    if (!isEuCountry(state.country)) {
      return "";
    }

    var effective = getEffectiveMode();
    return effective === "incl" ? CONFIG.labels.incl : CONFIG.labels.excl;
  }

  function applyNodeLabel(node, label) {
    if (!node || !node.parentElement) {
      return;
    }

    var parent = node.parentElement;
    if (parent.closest(NO_LABEL_CONTEXT_SELECTOR) || isUpnovaElement(parent)) {
      clearNodeLabel(node);
      return;
    }

    if (parent.closest(SKIP_CONTEXT_SELECTOR)) {
      return;
    }

    if (!parent.closest(PRICE_CONTEXT_SELECTOR)) {
      return;
    }

    if (!label) {
      clearNodeLabel(node);
      return;
    }

    if (parent.dataset.crdVatLabel !== label) {
      parent.dataset.crdVatLabel = label;
    }
    if (!parent.classList.contains("crd-vat-labeled")) {
      parent.classList.add("crd-vat-labeled");
    }
  }

  function clearNodeLabel(node) {
    if (!node || !node.parentElement) {
      return;
    }

    var parent = node.parentElement;
    if (
      !parent.classList.contains("crd-vat-labeled") &&
      !parent.dataset.crdVatLabel
    ) {
      return;
    }

    parent.classList.remove("crd-vat-labeled");
    delete parent.dataset.crdVatLabel;
  }

  function ensureSourceId(node) {
    if (!node || !node.dataset) {
      return "";
    }

    if (node.dataset.crdVatSourceId) {
      return node.dataset.crdVatSourceId;
    }

    state.sourceSeq += 1;
    node.dataset.crdVatSourceId = "crd-vat-src-" + String(state.sourceSeq);
    return node.dataset.crdVatSourceId;
  }

  function convertTextForShadow(text) {
    var sourceText = stripVatLabels(String(text || ""));
    var parsed = parsePriceTokens(sourceText, state.country);
    if (!parsed.tokens.length) {
      return "";
    }

    return renderTextWithoutLabel({
      sourceText: sourceText,
      tokens: parsed.tokens,
    });
  }

  function syncShadowNode(sourceNode, nextText) {
    if (!sourceNode || !sourceNode.parentElement) {
      return;
    }

    var sourceId = ensureSourceId(sourceNode);
    if (!sourceId) {
      return;
    }

    var parent = sourceNode.parentElement;
    var shadowNode = parent.querySelector(
      '[data-crd-vat-shadow-for="' + sourceId + '"]',
    );

    if (!nextText) {
      if (shadowNode) {
        shadowNode.remove();
      }
      if (sourceNode.classList.contains("crd-vat-source-hidden")) {
        sourceNode.classList.remove("crd-vat-source-hidden");
      }
      return;
    }

    if (!shadowNode) {
      shadowNode = document.createElement(sourceNode.tagName.toLowerCase());
      shadowNode.className =
        (sourceNode.className ? sourceNode.className + " " : "") +
        "crd-vat-shadow-node";
      shadowNode.setAttribute("data-crd-vat-shadow-for", sourceId);
      shadowNode.setAttribute("aria-hidden", "true");
      sourceNode.insertAdjacentElement("afterend", shadowNode);
    }

    if (shadowNode.textContent !== nextText) {
      shadowNode.textContent = nextText;
    }

    var shadowLabel = sourceNode.closest(NO_LABEL_CONTEXT_SELECTOR)
      ? ""
      : getCurrentLabelText();
    // Only write when something changes - identical writes still create mutation records
    // (which session recorders like Clarity then process).
    if (shadowLabel) {
      if (shadowNode.dataset.crdVatLabel !== shadowLabel) {
        shadowNode.dataset.crdVatLabel = shadowLabel;
      }
      if (!shadowNode.classList.contains("crd-vat-labeled-element")) {
        shadowNode.classList.add("crd-vat-labeled-element");
      }
    } else {
      if ("crdVatLabel" in shadowNode.dataset) {
        delete shadowNode.dataset.crdVatLabel;
      }
      if (shadowNode.classList.contains("crd-vat-labeled-element")) {
        shadowNode.classList.remove("crd-vat-labeled-element");
      }
    }
    if (!sourceNode.classList.contains("crd-vat-source-hidden")) {
      sourceNode.classList.add("crd-vat-source-hidden");
    }
  }

  function cleanupStaleShadowNodes() {
    var shadows = document.querySelectorAll(
      ".crd-vat-shadow-node[data-crd-vat-shadow-for]",
    );
    for (var i = 0; i < shadows.length; i += 1) {
      var shadow = shadows[i];
      var sourceId = shadow.getAttribute("data-crd-vat-shadow-for");
      if (!sourceId) {
        continue;
      }

      var source = document.querySelector(
        '[data-crd-vat-source-id="' + sourceId + '"]',
      );
      if (!source || !source.isConnected) {
        shadow.remove();
      }
    }
  }

  function syncClvShadowValues() {
    var selectors =
      Array.isArray(KACHING_SHADOW_SOURCE_SELECTORS) &&
      KACHING_SHADOW_SOURCE_SELECTORS.length
        ? KACHING_SHADOW_SOURCE_SELECTORS
        : [
            ".kaching-bundles__bar-price:not(.crd-vat-shadow-node)",
            ".kaching-bundles__bar-full-price:not(.crd-vat-shadow-node)",
            ".kaching-bundles__bundle-products__price:not(.crd-vat-shadow-node)",
            ".kaching-bundles__bundle-products__full-price:not(.crd-vat-shadow-node)",
            ".kaching-bundles__upsell__price:not(.crd-vat-shadow-node)",
            ".kaching-bundles__upsell__full-price:not(.crd-vat-shadow-node)",
            ".kaching-bundles__free-gift__price:not(.crd-vat-shadow-node)",
            ".kaching-bundles__free-gift__full-price:not(.crd-vat-shadow-node)",
            ".kaching-bundles__bar-label[data-clv-original-text]:not(.crd-vat-shadow-node)",
            ".clv-kaching-saved-total__text:not(.crd-vat-shadow-node)",
            ".crd-total-price:not(.crd-vat-shadow-node)",
          ];

    for (var i = 0; i < selectors.length; i += 1) {
      var nodes = document.querySelectorAll(selectors[i]);
      for (var j = 0; j < nodes.length; j += 1) {
        var node = nodes[j];
        var converted = convertTextForShadow(node.textContent || "");
        syncShadowNode(node, converted);
      }
    }

    cleanupStaleShadowNodes();
  }

  function syncUpezDistanceValues() {
    var selectors =
      Array.isArray(UPEZ_DISTANCE_CONTEXT_SELECTORS) &&
      UPEZ_DISTANCE_CONTEXT_SELECTORS.length
        ? UPEZ_DISTANCE_CONTEXT_SELECTORS
        : [".distance_label .builder-text", ".popover-container .builder-text"];

    for (var i = 0; i < selectors.length; i += 1) {
      var nodes = document.querySelectorAll(selectors[i]);
      for (var j = 0; j < nodes.length; j += 1) {
        processSpecialContainer(nodes[j]);
      }
    }
  }

  function processSpecialContainer(container) {
    if (!container) {
      return;
    }

    var walker = document.createTreeWalker(
      container,
      NodeFilter.SHOW_TEXT,
      null,
    );
    var node;
    while ((node = walker.nextNode())) {
      processSpecialTextNode(node);
    }
  }

  function processSpecialTextNode(node) {
    if (!node || !node.nodeValue || !node.parentElement) {
      return;
    }

    if (node.parentElement.closest(SKIP_CONTEXT_SELECTOR)) {
      if (state.specialNodeMeta.has(node)) {
        state.specialNodeMeta.delete(node);
      }
      return;
    }

    var current = String(node.nodeValue);
    var meta = state.specialNodeMeta.get(node);

    if (!meta || current !== meta.lastApplied) {
      var sourceText = stripVatLabels(current);
      var parsed = parsePriceTokens(sourceText, state.country);
      if (!parsed.tokens.length) {
        state.specialNodeMeta.delete(node);
        return;
      }

      meta = {
        sourceText: sourceText,
        tokens: parsed.tokens,
        lastApplied: "",
      };

      state.specialNodeMeta.set(node, meta);
    }

    var next = renderTextWithoutLabel(meta);
    if (next !== current) {
      node.nodeValue = next;
    }

    meta.lastApplied = next;
  }

  function stripVatLabels(text) {
    return String(text || "").replace(/\s+(?:Incl\. VAT|Excl\. VAT)/gi, "");
  }

  function safeStorageGet(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (_error) {
      return null;
    }
  }

  function safeStorageSet(key, value) {
    try {
      window.localStorage.setItem(key, String(value));
    } catch (_error) {
      return;
    }
  }

  function parseJsonObjectSafe(value) {
    if (!value) {
      return {};
    }

    try {
      var parsed = JSON.parse(value);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed;
      }
    } catch (_error) {
      return {};
    }

    return {};
  }

  function hasOwn(object, key) {
    return !!object && Object.prototype.hasOwnProperty.call(object, key);
  }

  var PRICE_CONTEXT_SELECTOR = [
    "price-list",
    "sale-price",
    "compare-at-price",
    ".price",
    ".money",
    "span.money",
    ".product-info__price",
    ".product-form__price",
    ".price-item",
    "[data-price]",
    "[data-product-price]",
    "[data-cart-item-regular-price]",
    "[data-cart-item-final-price]",
    "[data-cart-item-original-price]",
    "[data-cart-item-price]",
    "[data-cart-item-line-price]",
    "td.text-end.text-subdued",
    ".h-stack.gap-4.justify-between > span:last-child",
    ".cart__price",
    ".cart__final-price",
    ".cart-items__price",
    ".kaching-bundles__bar-price",
    ".kaching-bundles__bar-full-price",
    ".kaching-bundles__bundle-products__price",
    ".kaching-bundles__bundle-products__full-price",
    ".kaching-bundles__upsell__price",
    ".kaching-bundles__upsell__full-price",
    ".kaching-bundles__free-gift__price",
    ".kaching-bundles__free-gift__full-price",
    "upez-money-convert",
    ".upez--cart-item--discounted-price",
    ".upez--cart-item--original-price",
    ".upez--subtotal_price",
    "#you-save-badge",
  ].join(", ");

  var SKIP_CONTEXT_SELECTOR = [
    "[data-crd-vat-ui]",
    "script",
    "style",
    "noscript",
    "template",
    "svg",
    "math",
    "title",
    "textarea",
    "code",
    "pre",
    "[hidden]",
    '[aria-hidden=\"true\"]',
    ".visually-hidden",
    ".sr-only",
    ".crd-vat-source-hidden",
    ".crd-vat-shadow-node",
    "[data-product-json]",
    "[data-editor-layer-map]",
    "[data-metafield-layer-map]",
    "[data-layer-map-rows]",
    "[data-mini-gallery-json]",
  ].join(", ");

  var KACHING_CONTEXT_SELECTOR = [
    "kaching-bundle",
    "kaching-bundles-block",
    ".kaching-bundles",
    ".clv-kaching-bundle-pricing",
    ".clv-kaching-saved-total",
    ".crd-total-price",
  ].join(", ");

  var KACHING_SHADOW_SOURCE_SELECTORS = [
    ".kaching-bundles__bar-price:not(.crd-vat-shadow-node)",
    ".kaching-bundles__bar-full-price:not(.crd-vat-shadow-node)",
    ".kaching-bundles__bundle-products__price:not(.crd-vat-shadow-node)",
    ".kaching-bundles__bundle-products__full-price:not(.crd-vat-shadow-node)",
    ".kaching-bundles__upsell__price:not(.crd-vat-shadow-node)",
    ".kaching-bundles__upsell__full-price:not(.crd-vat-shadow-node)",
    ".kaching-bundles__free-gift__price:not(.crd-vat-shadow-node)",
    ".kaching-bundles__free-gift__full-price:not(.crd-vat-shadow-node)",
    ".kaching-bundles__bar-label[data-clv-original-text]:not(.crd-vat-shadow-node)",
    ".clv-kaching-saved-total__text:not(.crd-vat-shadow-node)",
    ".crd-total-price:not(.crd-vat-shadow-node)",
  ];

  var UPEZ_DISTANCE_CONTEXT_SELECTORS = [
    ".distance_label .builder-text",
    ".popover-container .builder-text",
  ];

  // Anything whose change can alter a displayed price (used to decide whether a DOM mutation needs a rescan).
  var RELEVANT_CONTEXT_SELECTOR = [
    PRICE_CONTEXT_SELECTOR,
    ".kaching-bundles__bar-label",
    ".clv-kaching-saved-total__text",
    ".crd-total-price",
    ".distance_label",
    ".popover-container",
  ].join(", ");

  var NO_LABEL_CONTEXT_SELECTOR = [
    '.upez--inner-drawer-container button[name="checkout"] .upez--subtotal_price',
    ".upez--inner-drawer-container .exemptify_button .upez--subtotal_price",
    "#you-save-badge",
    ".crd-ship-note",
  ].join(", ");

  var MONEY_TOKEN_REGEX =
    /(?:\b(?:EUR|USD|SEK)\s*-?\d[\d\s.,]*\d?)|(?:[\u20AC$\u00A3]\s*-?\d[\d\s.,]*\d?)|(?:-?\d[\d\s.,]*\d?\s*(?:\u20AC|\$|\u00A3|kr|SEK|EUR|USD))/gi;
})();
